// Tiny CSS-based animation shim — a drop-in for the small framer-motion subset
// the marketing site uses (entrance fades, whileInView reveals, an infinite
// float, and AnimatePresence). It removes framer-motion (~183 kB gzip) from the
// conversion-critical marketing chunk while preserving the visual feel.
// Supported props per `motion.<tag>`: initial / animate / whileInView / transition
// (duration, delay) / viewport (once, margin) / style / className. Array-valued
// `animate` (the hero float) maps to a CSS keyframe. Exit animations degrade to
// instant (AnimatePresence renders children) — layout/content are unchanged.
import { useEffect, useRef, useState, forwardRef, createElement } from "react";

const num = (v, unit = "px") => (typeof v === "number" ? `${v}${unit}` : v);

function transformStr(v) {
  if (!v) return "";
  const p = [];
  if (v.x != null && !Array.isArray(v.x)) p.push(`translateX(${num(v.x)})`);
  if (v.y != null && !Array.isArray(v.y)) p.push(`translateY(${num(v.y)})`);
  if (v.scale != null && !Array.isArray(v.scale)) p.push(`scale(${v.scale})`);
  if (v.rotate != null && !Array.isArray(v.rotate)) p.push(`rotate(${num(v.rotate, "deg")})`);
  return p.join(" ");
}
function frameStyle(v) {
  const s = {};
  if (!v) return s;
  if (v.opacity != null && !Array.isArray(v.opacity)) s.opacity = v.opacity;
  const t = transformStr(v);
  if (t) s.transform = t;
  return s;
}
const isLoop = (v) => v && Object.values(v).some(Array.isArray);

function makeMotion(tag) {
  return forwardRef(function M(props, ref) {
    const {
      initial, animate, whileInView, exit, transition = {}, viewport = {},
      style = {}, className = "", children, eager, ...rest
    } = props;

    const dur = transition.duration != null ? transition.duration : 0.5;
    const delay = transition.delay != null ? transition.delay : 0;
    const loop = isLoop(animate);
    const useInView = !!whileInView;

    // `eager` elements (above-the-fold / LCP-critical) render at their final
    // visible state on the very first paint — no opacity:0, no rAF wait.
    const [shown, setShown] = useState(loop || eager || (!useInView && !initial));
    const localRef = useRef(null);
    const setRefs = (node) => {
      localRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    useEffect(() => {
      if (loop || useInView || eager) return;
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }, [loop, useInView, eager]);

    useEffect(() => {
      if (!useInView) return;
      const node = localRef.current;
      if (!node) return;
      const once = viewport.once !== false;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) { setShown(true); if (once) io.disconnect(); }
            else if (!once) setShown(false);
          });
        },
        { rootMargin: viewport.margin || "0px", threshold: 0.1 }
      );
      io.observe(node);
      return () => io.disconnect();
    }, [useInView, viewport.once, viewport.margin]);

    let composed;
    if (loop) {
      composed = { ...style };
    } else {
      const shownTarget = whileInView || animate || {};
      const target = shown ? shownTarget : (initial || {});
      composed = {
        ...style,
        ...frameStyle(target),
        transition: `opacity ${dur}s ${delay}s ease, transform ${dur}s ${delay}s cubic-bezier(0.22,1,0.36,1)`,
      };
    }
    const cls = loop ? `${className} sc-float`.trim() : className;
    return createElement(tag, { ref: setRefs, className: cls, style: composed, ...rest }, children);
  });
}

const cache = {};
export const motion = new Proxy(
  {},
  { get: (_, tag) => (cache[tag] || (cache[tag] = makeMotion(tag))) }
);

// Exit animations degrade to instant; entrances on the incoming children still play.
export function AnimatePresence({ children }) {
  return children;
}

// Kept for API compatibility with the previous wrapper; now a no-op passthrough.
export function MotionProvider({ children }) {
  return children;
}
